import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createItem, useBackendStatus, useLiveConfig } from '../data/firestore';
import { useEntityList } from '../data/entities';
import { useAuth } from '../AuthContext';
import { useToast } from '../components/Toast';

// The demo domain page: a list of "items" (rename to whatever your app is
// about). Each item is a workspace with an S3 prefix, uploads and assets.

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};

const fmtWhen = (ms) => {
  if (!ms) return '';
  const d = Date.now() - ms;
  if (d < 60e3) return 'just now';
  if (d < 3600e3) return `${Math.floor(d / 60e3)}m ago`;
  if (d < 86400e3) return `${Math.floor(d / 3600e3)}h ago`;
  return new Date(ms).toLocaleDateString();
};

// Until the first build has shipped, the starter Items page is a
// placeholder — say so (audit P1-6) instead of letting the demo domain pass
// for the app. config/app `app` is what the studio synced down
// (shared/appCatalog.js firstBuild reads the same fields).
function FirstBuildNotice({ app }) {
  if (!app?.name && !app?.concept?.title) return null;
  const b = app.build || null;
  if (b?.deployedAt) return null;
  const what = app.concept?.title ? `the concept "${app.concept.title}"` : `"${app.name}"`;
  const stories = app.stories?.count ? ` and ${app.stories.count} user stor${app.stories.count === 1 ? 'y' : 'ies'}` : '';
  return (
    <div className="cs-card" style={{ padding: 14, marginTop: 16, borderColor: 'var(--info-border, var(--line))' }}>
      <strong>{app.name || app.concept?.title}</strong>
      <p className="cs-help" style={{ margin: '6px 0 0' }}>
        {b?.sessionId
          ? <>The first build is underway: a Dev Console session is building the screens from {what}{stories}. Until it is deployed, the list below is the kit's starter page, not the app.</>
          : b?.blocked?.reason
            ? <>The first build could not start: {b.blocked.reason}. The list below is the kit's starter page.</>
            : <>Screens are not built yet — this instance has {what}{stories} from the studio, and the list below is the kit's starter page. Ask the agent to "build the screens", or open the Dev Console › Build.</>}
      </p>
      <div style={{ marginTop: 8 }}><Link to="/dev" className="cs-btn cs-btn-outline cs-btn-sm">Dev Console › Build</Link></div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  // Tenant-aware list (orgId/workspaceId buckets) — see data/entities.js.
  const { docs: items } = useEntityList('item', {});
  const { doc: status } = useBackendStatus();
  const { doc: cfg } = useLiveConfig();
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  // undefined = status doc not loaded yet → say nothing rather than flash
  // "offline" for the first second of every visit.
  const backendLive = status === undefined ? null : !!(status?.heartbeat && Date.now() - status.heartbeat < 3 * 60 * 1000);

  return (
    <div className="cs-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0 }}>Items</h1>
        {backendLive !== null && (
          <span className="cs-help" title="Backend heartbeat, written every minute">
            {backendLive ? '● backend online' : '○ backend offline — uploads and jobs will wait'}
          </span>
        )}
      </div>

      {user && <FirstBuildNotice app={cfg?.app} />}

      {user && (
        <form
          style={{ display: 'flex', gap: 8, marginTop: 16, maxWidth: 480 }}
          onSubmit={async (e) => {
            e.preventDefault();
            if (!name.trim() || busy) return;
            setBusy(true);
            try {
              const id = await createItem({ name: name.trim() });
              setName('');
              // Land inside the new item: on the pg plane the list may lag
              // realtime, and the next step (upload) lives there anyway.
              navigate(`/items/${id}`);
            } catch (err) {
              toast(`Could not create item: ${err.message}`, 'danger', 8000);
            } finally {
              setBusy(false);
            }
          }}
        >
          <input
            className="cs-input"
            placeholder="New item name…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
          <button className="cs-btn cs-btn-primary" disabled={!name.trim() || busy} type="submit">
            {busy ? 'Creating…' : 'Create'}
          </button>
        </form>
      )}

      {items === null && <p className="cs-help" style={{ marginTop: 20 }}>Loading…</p>}
      {items && !items.length && (
        <div className="cs-empty" style={{ marginTop: 20 }}>
          Nothing here yet{user ? ' — create your first item above, then upload files into it.' : ' — sign in to create one.'}
        </div>
      )}

      <div style={{ display: 'grid', gap: 10, marginTop: 18 }}>
        {(items || []).map((it) => (
          <Link key={it.id} to={`/items/${it.id}`} className="cs-card" style={{ padding: 14, textDecoration: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <strong style={{ color: 'var(--text)' }}>{it.name}</strong>
              <span style={{ flex: 1 }} />
              <span className="cs-help" style={{ fontSize: 12 }}>
                {it.assetCount || 0} asset{(it.assetCount || 0) === 1 ? '' : 's'} · {fmtBytes(it.totalBytes)}
                {it.createdAt ? ` · ${fmtWhen(it.createdAt)}` : ''}
              </span>
            </div>
            {it.description && <p className="cs-help" style={{ marginTop: 6 }}>{it.description}</p>}
          </Link>
        ))}
      </div>
    </div>
  );
}
